import { type ReactNode, type Ref } from 'react'
import { GridList, GridListItem } from 'react-aria-components'
import { formatDistance, max } from 'date-fns'
import { LabelChip } from '@/components/Chip'
import { Icon } from '@/components/Icon'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import cx from '@/utils/classnames'
import { IconButton } from '@/components/IconButton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { AutoSkeleton } from '@/components/Skeleton'
import { ProgressBar } from '@/components/ProgressBar'
import type { Id } from '@/infra/id'
import { dateFormatters } from '@/utils/date-util'
import styles from './Jobs.module.css'
import { Badge } from '@/components/Badge'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from '@/components/EmptyState'

export interface JobsProps {
  children?: ReactNode
  testId?: string
}

/**
 * The Jobs
 */
export function Jobs({ children, testId }: JobsProps) {
  return (
    <GridList aria-label="Jobs" className={styles.root} data-test={testId}>
      {children}
    </GridList>
  )
}

export interface JobProps {
  id?: Id
  title: string
  href?: string
  variant?: 'default' | 'neutral'
  children?: ReactNode
  isPending: boolean
  isDisabled?: boolean
  testId?: string
}

/**
 *  The Job
 */
export function Job({
  isPending,
  title,
  href,
  variant = 'default',
  children,
  isDisabled,
  testId,
}: JobProps) {
  const { ref } = useSkeletonListContext()

  return (
    <GridListItem
      href={href}
      textValue={title}
      ref={ref as Ref<HTMLDivElement>}
      isDisabled={isDisabled}
      data-test={testId}
      className={({ isPressed, isHovered, isDisabled, isFocusVisible }) =>
        cx(
          styles.job,
          variant === 'neutral' && styles.neutral,
          isPending && styles.pending,
          isPressed && styles.pressed,
          isHovered && styles.hover,
          isDisabled && styles.disabled,
          isFocusVisible && styles.focusVisible,
        )
      }
    >
      {children}
    </GridListItem>
  )
}

export interface JobHeaderProps {
  serviceTypeLabel: string
  now: Date
  updatedAt: Date
  isSkeleton?: boolean
  onArchive?: () => void
  isDisabled?: boolean
  badgeCount?: number
  archiveButtonTestId?: string
}

/**
 * The JobHeader
 */
export function JobHeader({
  serviceTypeLabel,
  now,
  updatedAt,
  isSkeleton = false,
  onArchive,
  isDisabled,
  badgeCount,
  archiveButtonTestId,
}: JobHeaderProps) {
  const isSpacious = useIsSpacious()
  // Ensure `now` is never earlier than the `updatedAt` when formatting
  now = max([now, updatedAt])
  const ago = formatDistance(updatedAt, now, { addSuffix: true })
  const { vary } = useSkeletonListContext()

  return (
    <div className={styles.jobHeader}>
      <LabelChip isSkeleton={isSkeleton} text={serviceTypeLabel} />
      <div className="flex items-center gap-md">
        <Badge hidden={!badgeCount}>{badgeCount}</Badge>

        {isSpacious && (
          <div className="text-body-label">
            <AutoSkeleton characters={vary(15)} isSkeleton={isSkeleton}>
              Last updated {ago}
            </AutoSkeleton>
          </div>
        )}
        {onArchive && (
          <IconButton
            name="delete"
            label="Delete"
            variant="no-hover"
            isSkeleton={isSkeleton}
            isDisabled={isDisabled}
            onPress={() => onArchive()}
            testId={archiveButtonTestId}
          />
        )}
      </div>
    </div>
  )
}

export interface JobContentProps {
  title: string
  subtitle: string
  isSkeleton?: boolean
  children?: ReactNode
  titleTestId?: string
  subtitleTestId?: string
}

/**
 * The JobContent
 */
export function JobContent({
  title,
  subtitle,
  isSkeleton = false,
  children,
  titleTestId,
  subtitleTestId,
}: JobContentProps) {
  const { vary } = useSkeletonListContext()

  return (
    <div className={styles.jobContent}>
      <div className="text-body-base mb-md" data-test={subtitleTestId}>
        <AutoSkeleton characters={vary(20)} isSkeleton={isSkeleton}>
          {subtitle}
        </AutoSkeleton>
      </div>
      <h2 className="text-display-md" data-test={titleTestId}>
        <AutoSkeleton characters={vary(30)} isSkeleton={isSkeleton}>
          {title}
        </AutoSkeleton>
      </h2>
      <div className={styles.jobContentType}>{children}</div>
    </div>
  )
}

export interface JobFooterProps {
  label: string
  isSkeleton?: boolean
}

/**
 * The JobFooter
 */
export function JobFooter({ label, isSkeleton = false }: JobFooterProps) {
  const { vary } = useSkeletonListContext()

  return (
    <div className={styles.jobFooter}>
      <span className={styles.link}>
        <AutoSkeleton characters={vary(20)} isSkeleton={isSkeleton}>
          {label}
        </AutoSkeleton>
      </span>
      <AutoSkeleton characters={vary(5)} isSkeleton={isSkeleton}>
        <Icon name="chevron" />
      </AutoSkeleton>
    </div>
  )
}

export interface TaxJobProps {
  id: Id
  title: string
  subtitle: string
  actionText: string
  serviceTypeLabel: string
  updatedAt: Date
  now: Date
  progress: number
  href?: string
  isPending?: boolean
  isDisabled?: boolean
  badgeCount?: number
  testId?: string
}

/**
 * The TaxJob
 */
export function TaxJob({
  id,
  title,
  subtitle,
  actionText,
  serviceTypeLabel,
  updatedAt,
  now,
  progress,
  href,
  isPending = false,
  isDisabled,
  badgeCount,
  testId,
}: TaxJobProps) {
  return (
    <Job
      id={id}
      title={title}
      href={href}
      isPending={isPending}
      isDisabled={isDisabled}
      testId={testId}
    >
      <JobHeader
        now={now}
        serviceTypeLabel={serviceTypeLabel}
        updatedAt={updatedAt}
        badgeCount={badgeCount}
      />
      <JobContent
        title={title}
        subtitle={subtitle}
        titleTestId={testId ? `${testId}-title` : undefined}
        subtitleTestId={testId ? `${testId}-subtitle` : undefined}
      >
        <ProgressBar aria-label="Progress" progress={progress} />
      </JobContent>
      <JobFooter label={actionText} />
    </Job>
  )
}

/**
 * The ConsultationJob
 */
export interface ConsultationJobProps {
  id: Id
  title: string
  subtitle: string
  serviceTypeLabel: string
  href?: string
  updatedAt: Date
  now: Date
  startTime?: Date
  isPendingCall?: boolean
  isPending?: boolean
  isDisabled?: boolean
  badgeCount?: number
  testId?: string
}

export function ConsultationJob({
  id,
  title,
  subtitle,
  serviceTypeLabel,
  href,
  startTime,
  updatedAt,
  now,
  isPendingCall,
  isPending = false,
  isDisabled,
  badgeCount,
  testId,
}: ConsultationJobProps) {
  return (
    <Job
      id={id}
      title={title}
      href={href}
      isPending={isPending}
      isDisabled={isDisabled}
      testId={testId}
    >
      <JobHeader
        now={now}
        serviceTypeLabel={serviceTypeLabel}
        updatedAt={updatedAt}
        badgeCount={badgeCount}
      />
      <JobContent
        title={title}
        subtitle={subtitle}
        titleTestId={testId ? `${testId}-title` : undefined}
        subtitleTestId={testId ? `${testId}-subtitle` : undefined}
      >
        {isPendingCall && startTime && (
          <div className={styles.calendar}>
            <div className="flex items-center">
              <Icon name="calendar-tick" />
              <span>{dateFormatters.dateLong.format(startTime)}</span>
            </div>

            <div className="flex items-center">
              <Icon name="time" />
              <span>{dateFormatters.timeDefault.format(startTime)}</span>
            </div>
          </div>
        )}
      </JobContent>
      <JobFooter label={isPendingCall ? 'Join call' : 'See details'} />
    </Job>
  )
}

export interface BookkeepingJobProps {
  id: Id
  title: string
  subtitle: string
  serviceTypeLabel: string
  href?: string
  updatedAt: Date
  now: Date
  completedCount: number
  total: number
  isPending?: boolean
  isDisabled?: boolean
  badgeCount?: number
  testId?: string
}

/**
 * The BookkeepingJob
 */
export function BookkeepingJob({
  id,
  title,
  subtitle,
  serviceTypeLabel,
  href,
  updatedAt,
  now,
  completedCount,
  total,
  isPending = false,
  isDisabled,
  badgeCount,
  testId,
}: BookkeepingJobProps) {
  const progress = total > 0 ? (completedCount / total) * 100 : 0

  return (
    <Job
      id={id}
      title={title}
      href={href}
      isPending={isPending}
      isDisabled={isDisabled}
      testId={testId}
    >
      <JobHeader
        now={now}
        serviceTypeLabel={serviceTypeLabel}
        updatedAt={updatedAt}
        badgeCount={badgeCount}
      />
      <JobContent
        title={title}
        subtitle={subtitle}
        titleTestId={testId ? `${testId}-title` : undefined}
        subtitleTestId={testId ? `${testId}-subtitle` : undefined}
      >
        <div className="flex items-center gap-2xl">
          <div className="flex-1">
            <ProgressBar aria-label="Progress" progress={progress} />
          </div>
          <div className="text-interactive-link">
            {completedCount}/{total}
          </div>
        </div>
      </JobContent>
      <JobFooter label="See details" />
    </Job>
  )
}

export interface UnderConstructionJobProps {
  id: Id
  title: string
  subtitle: string
  serviceTypeLabel: string
  updatedAt: Date
  now: Date
  href?: string
  onArchive?: () => void
  isPending?: boolean
  isDisabled?: boolean
  badgeCount?: number
  testId?: string
  archiveButtonTestId?: string
}

/**
 * The UnderConstructionJob
 */
export function UnderConstructionJob({
  id,
  title,
  subtitle,
  serviceTypeLabel,
  updatedAt,
  now,
  href,
  onArchive,
  isPending = false,
  isDisabled,
  badgeCount,
  testId,
  archiveButtonTestId,
}: UnderConstructionJobProps) {
  return (
    <Job
      id={id}
      title={title}
      variant="neutral"
      href={href}
      isPending={isPending}
      isDisabled={isDisabled}
      testId={testId}
    >
      <JobHeader
        now={now}
        serviceTypeLabel={serviceTypeLabel}
        updatedAt={updatedAt}
        onArchive={onArchive}
        isDisabled={isDisabled}
        badgeCount={badgeCount}
        archiveButtonTestId={archiveButtonTestId}
      />
      <JobContent
        title={title}
        subtitle={subtitle}
        titleTestId={testId ? `${testId}-title` : undefined}
        subtitleTestId={testId ? `${testId}-subtitle` : undefined}
      />
      <JobFooter label="Answer remaining questions" />
    </Job>
  )
}

export interface DefaultJobProps {
  id: Id
  title: string
  subtitle: string
  serviceTypeLabel: string
  updatedAt: Date
  now: Date
  href?: string
  isPending?: boolean
  isDisabled?: boolean
  badgeCount?: number
  testId?: string
}

/**
 * The DefaultJob
 */
export function DefaultJob({
  id,
  title,
  subtitle,
  serviceTypeLabel,
  href,
  updatedAt,
  now,
  isPending = false,
  isDisabled,
  badgeCount,
  testId,
}: DefaultJobProps) {
  return (
    <Job
      id={id}
      title={title}
      href={href}
      isPending={isPending}
      isDisabled={isDisabled}
      testId={testId}
    >
      <JobHeader
        now={now}
        serviceTypeLabel={serviceTypeLabel}
        updatedAt={updatedAt}
        badgeCount={badgeCount}
      />
      <JobContent
        title={title}
        subtitle={subtitle}
        titleTestId={testId ? `${testId}-title` : undefined}
        subtitleTestId={testId ? `${testId}-subtitle` : undefined}
      />
      <JobFooter label="See details" />
    </Job>
  )
}

/**
 * The JobSkeleton
 */
export function JobSkeleton() {
  return (
    <Job title="Job" variant="neutral" isPending={false}>
      <JobHeader
        now={new Date()}
        serviceTypeLabel="Service type"
        updatedAt={new Date()}
        isSkeleton
      />
      <JobContent title="Title" subtitle="Subtitle" isSkeleton />
      <JobFooter label="Label" isSkeleton />
    </Job>
  )
}

/**
 * Empty state to show for users who cannot create jobs.
 */
export function MissingJobCreatePermissionEmptyState() {
  return (
    <EmptyState>
      <EmptyStateTitle>No current jobs</EmptyStateTitle>
      <EmptyStateDescription>
        You can't create jobs right now. Someone should be reaching out shortly
        to assist you, or if you think this is a mistake, please contact our
        customer service team.
      </EmptyStateDescription>
    </EmptyState>
  )
}
